import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot
} from '@angular/router';
import { map, take } from 'rxjs/operators';
import { PermissionCheckService } from '../Theme/shared/service/permission-check.service';
import { CustomToastService } from '../Theme/shared/service/toast.service';
import { TranslateService } from '@ngx-translate/core';

/** Route data keys for PermissionGuard */
export const ROUTE_DATA_PERMISSION = 'permission';
export const ROUTE_DATA_PERMISSIONS = 'permissions';
export const ROUTE_DATA_PERMISSION_MODE = 'permissionMode'; // 'any' | 'all'
export const ROUTE_DATA_PERMISSION_REDIRECT = 'permissionRedirect';

@Injectable({
  providedIn: 'root'
})
export class PermissionGuard implements CanActivate {
  constructor(
    private router: Router,
    private permissionCheck: PermissionCheckService,
    private toast: CustomToastService,
    private translate: TranslateService
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    const permission = route.data[ROUTE_DATA_PERMISSION] as string | undefined;
    const permissions = route.data[ROUTE_DATA_PERMISSIONS] as string[] | undefined;
    const mode = (route.data[ROUTE_DATA_PERMISSION_MODE] as 'any' | 'all') || 'any';
    /** Only redirect when the route explicitly defines permissionRedirect; otherwise block access without redirecting. */
    const redirectTo = route.data[ROUTE_DATA_PERMISSION_REDIRECT] as string | undefined;

    const codes: string[] = permission ? [permission] : permissions || [];
    if (codes.length === 0) {
      return true;
    }

    const check$ =
      mode === 'all'
        ? this.permissionCheck.hasAllPermissions(codes)
        : this.permissionCheck.hasAnyPermission(codes);

    return check$.pipe(
      take(1),
      map((allowed) => {
        if (allowed) return true;
        const message = this.translate.instant('PermissionGuard.noAccess') || 'You do not have access to this page.';
        this.toast.showWarning(message, this.translate.instant('PermissionGuard.noAccessTitle') || 'Access denied', { timeOut: 5000 });
        if (redirectTo) {
          this.router.navigateByUrl(redirectTo);
        }
        return false;
      })
    );
  }
}
